import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import { Shield, ArrowLeft, Copy, Check } from "lucide-react";
import { twoFactorService } from "../services/twoFactor.service";
import pmLogo from "../assets/login/Logo.png";
import styles from "./TwoFactorSetupPage.module.css";

const TwoFactorSetupPage: React.FC = () => {
  const [step, setStep] = useState(1); // 1: Setup, 2: Verify, 3: Complete
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = parseInt(searchParams.get("userId") || "0");
  const isEnforced = searchParams.get("enforced") === "true";

  useEffect(() => {
    if (!userId) {
      navigate("/login");
    }
  }, [userId, navigate]);

  const handleSetup = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await twoFactorService.setupTwoFactor(userId);
      setQrCode(response.data.qrCode);
      setSecret(response.data.secret);
      setStep(2);
      message.success("Thiết lập 2FA thành công! Vui lòng quét QR code.");
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Lỗi khi thiết lập 2FA");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      message.error("Vui lòng nhập mã 6 số");
      return;
    }

    if (!userId) return;

    setLoading(true);
    try {
      const response = await twoFactorService.verifyTwoFactorSetup(userId, twoFactorCode);
      if (response.data.backupCodes) {
        setBackupCodes(response.data.backupCodes);
      }
      setStep(3);
      message.success("Xác thực 2FA thành công! 2FA đã được kích hoạt.");
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Mã xác thực không đúng");
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setTwoFactorCode(value);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    message.success("Đã copy vào clipboard!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (!userId) {
    return null;
  }

  if (step === 3) {
    return (
      <div className={styles.container}>
        <div className={styles.rightColumn}>
          <div className={`${styles.formContainer} ${styles.formContainerCompact}`}>
            <img src={pmLogo} alt="Logo" className={styles.logo} />
            <h2 className={styles.title}>2FA Đã Được Kích Hoạt!</h2>
            <p className={styles.description}>
              Bây giờ bạn cần nhập mã 2FA mỗi khi đăng nhập.
            </p>
            <div className={styles.backupCodesSection}>
              <h3>Mã Backup (Lưu lại an toàn)</h3>
              <p className={styles.warning}>
                Lưu lại các mã này ở nơi an toàn. Bạn có thể sử dụng chúng để đăng nhập nếu mất điện thoại.
              </p>
              <div className={styles.backupCodesGrid}>
                {backupCodes.map((code, index) => (
                  <div key={index} className={styles.backupCode}>
                    <span>{code}</span>
                    <button
                      onClick={() => copyToClipboard(code)}
                      className={styles.copyBtn}
                    >
                      {copiedCode === code ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => navigate(`/verify-2fa?userId=${userId}`, { replace: true })}
              className={styles.submitButton}
            >
              Tiếp Tục Đăng Nhập
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className={styles.container}>
        <div className={styles.rightColumn}>
          <button
            type="button"
            onClick={() => setStep(1)}
            className={styles.backButton}
          >
            <ArrowLeft size={18} />
            <span>{isEnforced ? "Về đăng nhập" : "Quay lại"}</span>
          </button>
          <div className={styles.formContainer}>
            <img src={pmLogo} alt="Logo" className={styles.logo} />
            <h2 className={styles.title}>Xác Thực 2FA</h2>
            <p className={styles.description}>
              Nhập mã 6 số từ ứng dụng xác thực để kích hoạt 2FA
            </p>
            <div className={styles.qrSection}>
              <h3>QR Code</h3>
              <div className={styles.qrContainer}>
                <img src={qrCode} alt="QR Code" className={styles.qrImage} />
              </div>
              {secret && (
                <div className={styles.manualKeySection}>
                  <h4>Hoặc nhập thủ công</h4>
                  <div className={styles.secretContainer}>
                    <code className={styles.secretCode}>{secret}</code>
                    <button
                      onClick={() => copyToClipboard(secret)}
                      className={styles.copyBtn}
                    >
                      {copiedCode === secret ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.verificationSection}>
              <h3>Nhập Mã Xác Thực</h3>
              <input
                type="text"
                placeholder="000000"
                value={twoFactorCode}
                onChange={handleTwoFactorChange}
                className={styles.input}
                maxLength={6}
                disabled={loading}
                autoFocus
              />
              <button
                onClick={handleVerify}
                disabled={loading || twoFactorCode.length !== 6}
                className={styles.submitButton}
              >
                {loading ? "Đang xác thực..." : "Xác Thực & Kích Hoạt"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.rightColumn}>
        <button
          type="button"
          onClick={() => navigate("/login")}
          className={styles.backButton}
        >
          <ArrowLeft size={18} />
          <span>{isEnforced ? "Về đăng nhập" : "Quay lại"}</span>
        </button>
        <div className={styles.formContainer}>
          <img src={pmLogo} alt="Logo" className={styles.logo} />
          <div className={styles.header}>
            <Shield size={48} className={styles.icon} />
            <h2 className={styles.title}>Thiết Lập 2FA</h2>
            <p className={styles.description}>
              {isEnforced
                ? "Để tiếp tục đăng nhập, bạn cần bật xác thực 2 lớp cho tài khoản này."
                : "Bảo vệ tài khoản của bạn bằng xác thực 2 yếu tố"}
            </p>
          </div>
          <div className={styles.infoSection}>
            <h3>Lợi Ích của 2FA</h3>
            <ul>
              <li>Bảo mật cao hơn - Cần cả mật khẩu và mã 2FA</li>
              <li>Bảo vệ khỏi tấn công mật khẩu</li>
              <li>Mã thay đổi mỗi 30 giây</li>
              <li>Tương thích với Google Authenticator, Microsoft Authenticator</li>
            </ul>
          </div>
          <button
            onClick={handleSetup}
            disabled={loading}
            className={styles.submitButton}
          >
            {loading ? "Đang thiết lập..." : "Bắt Đầu Thiết Lập"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSetupPage;
